import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { Client } from "../generated/api-client";
import { getJwtTimeToExpiryMs } from "../utils/jwt";
import {
  notifyModelUnavailable,
  parseAxiosError,
} from "../utils/llmAvailability";

const API_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL
  ? `${process.env.REACT_APP_PUBLIC_BASE_URL.replace(/\/$/, "")}/api`
  : "http://localhost:8000";

export { API_BASE_URL };

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 200000,
  headers: {
    "Content-Type": "application/json",
  },
  // Refresh token in cookie HttpOnly: inviato solo con withCredentials.
  withCredentials: true,
});

const REFRESH_LEEWAY_MS = 120_000;
const MIN_REFRESH_DELAY_MS = 5_000;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Programma il refresh proattivo dell'access token in base al claim `exp`.
 */
export const scheduleTokenRefresh = (accessToken: string | null | undefined) => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (!accessToken) return;

  const ttl = getJwtTimeToExpiryMs(accessToken);
  if (ttl === null) return;

  const delay = Math.max(ttl - REFRESH_LEEWAY_MS, MIN_REFRESH_DELAY_MS);
  refreshTimer = setTimeout(() => {
    void proactiveRefresh();
  }, delay);
};

const proactiveRefresh = async (): Promise<void> => {
  if (isRefreshing) return;

  isRefreshing = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      { timeout: 30_000, withCredentials: true },
    );
    const { access_token } = response.data;
    localStorage.setItem("access_token", access_token);
    processQueue(null, access_token);
    scheduleTokenRefresh(access_token);
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem("access_token");
      processQueue(err, null);
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
    } else {
      processQueue(err, null);
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        void proactiveRefresh();
      }, 30_000);
    }
  } finally {
    isRefreshing = false;
  }
};

scheduleTokenRefresh(localStorage.getItem("access_token"));

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    const token = localStorage.getItem("access_token");
    if (!token) return;
    const ttl = getJwtTimeToExpiryMs(token);
    if (ttl === null) return;
    if (ttl < REFRESH_LEEWAY_MS) {
      void proactiveRefresh();
    } else {
      scheduleTokenRefresh(token);
    }
  });
}

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("access_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const url = originalRequest?.url || "";

    if (
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { timeout: 30_000, withCredentials: true },
        );

        const { access_token } = response.data;
        localStorage.setItem("access_token", access_token);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        processQueue(null, access_token);
        isRefreshing = false;
        scheduleTokenRefresh(access_token);

        return axiosInstance(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError, null);
        isRefreshing = false;

        const refreshStatus = refreshError?.response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          localStorage.removeItem("access_token");
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
        }
        return Promise.reject(refreshError);
      }
    }

    const parsed = parseAxiosError(error);
    if (parsed.isModelUnavailable) {
      notifyModelUnavailable(parsed.message);
      (error as AxiosError & { isModelUnavailable?: boolean }).isModelUnavailable = true;
    }

    return Promise.reject(error);
  }
);

export const apiClient = new Client(API_BASE_URL, axiosInstance);
